import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  TextField,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { AutoAwesome as CreateIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import StoreHeader from '../../components/layout/StoreHeader';
import CardQuickViewModal from '../../components/CardQuickViewModal';
import CardCoverImage from '../../components/CardCoverImage';
import FeelingStep from '../../components/create/FeelingStep';
import StyleStep from '../../components/create/StyleStep';
import EmailRecoveryPrompt from '../../components/EmailRecoveryPrompt';
import SuggestionField from '../../components/SuggestionField';
import {
  addCreation,
  getSessionId,
  addPendingCreation,
  removePendingCreation,
} from '../../lib/myCreations';
// The generator payload is assembled OUTSIDE this component on purpose — see generateRequest.js. It is
// pinned by a guard test so the create-flow rework cannot quietly change what the image model is told.
import { buildGenerateRequest, classifyChange, allFeelings as joinFeelings } from './generateRequest';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Wizard taxonomy mirrors the approved create mockup. The Feeling step's taxonomy lives in
// lib/feelingTaxonomy.js (the 8-family accordion, rendered by components/create/FeelingStep); the
// Style step's 18 styles live in lib/styleTaxonomy.js (rendered by components/create/StyleStep).
const WIZARD_STEPS = ['words', 'photo', 'feeling', 'style', 'review'];

// How the uploaded photo should be used (BR-CREATE-09). Shown only when a photo is present.
// 'include' (place the real photo / compositing) is BR-CREATE-10 — staged, so it's disabled.
const PHOTO_USAGE_OPTIONS = [
  { id: 'reference', label: 'Use as a reference', help: 'Loose inspiration — we’ll draw from it, not copy it.' },
  { id: 'likeness', label: 'Recreate their likeness', help: 'A photoreal portrait that preserves their appearance.' },
  { id: 'style', label: 'Match its colors & mood', help: 'Borrow the palette and feeling, not the subject.' },
  { id: 'include', label: 'Place the actual photo on the card', help: 'Use the photo itself as the front of the card.' },
];
const PHOTO_USAGE_LABELS = PHOTO_USAGE_OPTIONS.reduce((acc, o) => ({ ...acc, [o.id]: o.label }), {});

const TEAL = '#206884';

// "Make it yours" quick chips (approved mockup): each inserts calm starter text into the change box so
// the shopper can finish the sentence in their own words. Dignified phrasing (memorial context).
const CHANGE_CHIPS = [
  { label: 'Change the words', starter: 'Change the words to say ' },
  { label: 'Add a name', starter: 'Add the name ' },
  { label: 'Swap the artwork', starter: 'Change the artwork to ' },
  { label: 'Different colors', starter: 'Use different colors: ' },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

async function postJSON(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

async function getJSON(path) {
  const res = await fetch(`${API_BASE}${path}`, { method: 'GET' });
  return res.json();
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Async generation transport (Generation v3 · Step 3): the premium model (GPT Image 2) takes ~30–120s —
// and the verify/regenerate loop can legitimately run several minutes — so /generate returns a
// generationId and we POLL the status endpoint until the background worker finishes. ~3s cadence.
// C1 (2026-07-16): the cap is set COMFORTABLY ABOVE the server's own budget (OPENAI_JOB_DEADLINE_MS 780s
// + overhead) so the client NEVER gives up while the job is still legitimately running (the old 180s cap
// threw a "try again" at 3 min mid-job → invited a duplicate PAID generation). The server always resolves
// the job (done/failed with a friendly message) before this backstop; the status endpoint is the truth.
export const POLL_INTERVAL_MS = 3000;
export const POLL_MAX_MS = 840000;
// After this long, switch the wait copy from "a couple of minutes" to the honest browse-away line (the
// job is fine; gpt-image-2 is just slow). Purely cosmetic — it never ends the wait.
export const WAIT_STILL_WORKING_MS = 75000;
// Sentinel thrown ONLY if the backstop cap is reached while the job is STILL pending server-side (rare —
// the server resolves first). The wait ends WITHOUT a try-again error: the design keeps running and lands
// in My creations (browse-away), so we never invite a duplicate paid generation.
export const POLL_STILL_WORKING = 'STILL_WORKING';

/** Which wait-screen copy stage to show for a given elapsed time (pure, testable). */
export function resolveWaitStage(elapsedMs) {
  return elapsedMs >= WAIT_STILL_WORKING_MS ? 'still-working' : 'early';
}

async function pollGeneration(generationId) {
  const deadline = Date.now() + POLL_MAX_MS;
  // First poll immediately (a fast/mock generation may already be done), then back off to the cadence.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const status = await getJSON(`/api/cards/generate/${generationId}`);
    if (status && status.success && status.data) {
      if (status.data.status === 'done') return status.data;
      // Only a SERVER 'failed' ends the wait with an error — and it already carries a friendly message.
      // A 'pending' status NEVER throws a try-again (that was the duplicate-generation bug).
      if (status.data.status === 'failed') {
        throw new Error(status.data.error || 'Generation failed');
      }
    } else if (status && status.success === false) {
      throw new Error(status.error || 'Generation failed');
    }
    // Backstop only: if we somehow reach the cap while still pending, hand off to browse-away — never a
    // "please try again" (the job is still running server-side; it will land in My creations).
    if (Date.now() >= deadline) {
      throw new Error(POLL_STILL_WORKING);
    }
    await sleep(POLL_INTERVAL_MS);
  }
}

const CreatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // "Create one like this" (approved mockup create-landing-flow): `?like=<cardId>` seeds a card from a
  // source store card — its recipe is silently preselected and the card image becomes the visual
  // reference. Absent → the ordinary wizard (unchanged fallback).
  const referenceCardId = searchParams.get('like') || null;

  // 'makeYours' | 'wizard' | 'generating' | 'ideas'. With a `?like=` source we land on the seeded
  // "Make it yours" screen; otherwise straight on the wizard (the how-it-works modal is the front door).
  const [phase, setPhase] = useState(referenceCardId ? 'makeYours' : 'wizard');
  const [wizardStep, setWizardStep] = useState(0);
  // Quick-view of a generated option (shared modal). Holds the option id being previewed.
  const [quickViewOptionId, setQuickViewOptionId] = useState(null);

  // Collected inputs
  const [feelings, setFeelings] = useState([]);
  const [ownFeeling, setOwnFeeling] = useState('');
  const [styleId, setStyleId] = useState('');
  // The words step collects TWO separate things: the words printed ON the cover (a real type
  // layer, never sent to the image model) and what the ARTWORK should show (imagery, feeds the prompt).
  const [coverText, setCoverText] = useState('');
  const [imageryDescription, setImageryDescription] = useState('');
  // How the uploaded photo is used (BR-CREATE-09). Defaults to 'reference' once a photo is added.
  const [photoUsage, setPhotoUsage] = useState('reference');
  const [photoNote, setPhotoNote] = useState('');

  // Photo upload (source for image-to-image / likeness)
  const [photoKey, setPhotoKey] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState(null);

  // "Make it yours": the free-text change request + the source card's cover (for the reference thumb).
  const [changeInstruction, setChangeInstruction] = useState('');
  const [referenceCover, setReferenceCover] = useState(() => ({
    coverImageUrl: (location.state && location.state.likeCoverImageUrl) || null,
    coverImageId: null,
    name: (location.state && location.state.likeName) || 'this card',
  }));

  // Generation result
  const [generation, setGeneration] = useState(null); // { generationId, options, provenance }
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  // Elapsed time on the wait screen (drives the calm copy switch after the early window — C1).
  const [genElapsedMs, setGenElapsedMs] = useState(0);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Soft-cap reached (ENFORCE mode only): show the value-framed email-to-continue gate, never a paywall.
  // In OBSERVE mode (default) the server never returns this, so the gate never appears.
  const [capReached, setCapReached] = useState(false);
  // Browse-away UX (Phase B): optional "email me when it's ready". Captured at REVIEW so it rides along
  // on the /generate request (the job stores it and notifies once on completion). Off by default.
  const [notifyWhenReady, setNotifyWhenReady] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');

  const obituaryId = useMemo(() => {
    const fromQuery = searchParams.get('id');
    if (fromQuery) return fromQuery;
    try {
      return localStorage.getItem('obituaryId') || null;
    } catch {
      return null;
    }
  }, [searchParams]);

  const deceasedDetails = useMemo(() => {
    try {
      const raw = localStorage.getItem('obituary');
      if (!raw) return undefined;
      const o = JSON.parse(raw);
      return {
        name: o.deceasedName || o.name,
        dates: o.dateOfDeath || undefined,
        bio: o.biography || o.bio || undefined,
      };
    } catch {
      return undefined;
    }
  }, []);

  useEffect(() => {
    // 'likeness' keeps the existing "auto-implies Photographic style" behavior.
    if (photoUsage === 'likeness' && photoKey && styleId === '') setStyleId('Photographic');
  }, [photoUsage, photoKey, styleId]);

  // "Create one like this": on mount with a `?like=` source, fetch the card and SILENTLY seed its recipe
  // (feeling / style / imagery) from provenance.inputs, and resolve its cover for the reference thumb.
  // Best-effort — a missing/recipe-less card (e.g. a catalog card) just leaves the change box to drive.
  useEffect(() => {
    if (!referenceCardId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getJSON(`/api/cards/${referenceCardId}`);
        if (cancelled || !res || !res.success || !res.data) return;
        const card = res.data;
        const inputs = (card.provenance && card.provenance.inputs) || {};
        if (Array.isArray(inputs.feeling) && inputs.feeling.length) setFeelings(inputs.feeling);
        if (typeof inputs.styleId === 'string' && inputs.styleId) setStyleId(inputs.styleId);
        const seededImagery = inputs.imageryDescription || inputs.instructions;
        if (typeof seededImagery === 'string' && seededImagery.trim()) {
          setImageryDescription(seededImagery.trim());
        }
        setReferenceCover((prev) => ({
          coverImageUrl: prev.coverImageUrl || card.coverImageUrl || null,
          coverImageId: card.coverImageId || null,
          name: prev.name && prev.name !== 'this card' ? prev.name : card.title || card.name || 'this card',
        }));
      } catch {
        // Seeding is best-effort; the reference image + change box still drive generation.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [referenceCardId]);

  // A quick chip inserts calm starter text into the change box (append with spacing; focus stays natural).
  const appendChangeStarter = (starter) =>
    setChangeInstruction((prev) => {
      const base = prev.trim();
      return base ? `${base}\n${starter}` : starter;
    });

  // Photo-on-card (BR-CREATE-10): the uploaded photo IS the design — no AI, no batch of 4 to pick.
  const isPhotoOnCard = !!photoKey && photoUsage === 'include';

  // While generating (AI path only), tick an elapsed clock so the wait copy can calmly switch from "a
  // couple of minutes" to the honest browse-away line after the early window (C1). Reset on entry/exit.
  useEffect(() => {
    if (phase !== 'generating' || isPhotoOnCard) {
      setGenElapsedMs(0);
      return undefined;
    }
    const started = Date.now();
    const t = setInterval(() => setGenElapsedMs(Date.now() - started), 1000);
    return () => clearInterval(t);
  }, [phase, isPhotoOnCard]);

  const toggleFeeling = (f) =>
    setFeelings((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  const handlePhotoChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset the input so re-selecting the same file still fires change.
    e.target.value = '';
    if (!file) return;

    setPhotoError(null);
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Open create-flow upload (the app-wide /api/media/upload is JWT-gated and 401s here).
      const res = await fetch(`${API_BASE}/api/cards/upload-photo`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json().catch(() => null);
      const key = data && data.data && data.data.key;
      if (!res.ok || !key) {
        throw new Error((data && data.error) || 'Upload failed');
      }
      // The folder-qualified storage key is what the backend re-fetches for image-to-image.
      setPhotoKey(key);
      let preview = '';
      try {
        if (typeof URL !== 'undefined' && URL.createObjectURL) {
          preview = URL.createObjectURL(file);
        }
      } catch (e) {
        preview = '';
      }
      setPhotoUrl(preview);
    } catch (err) {
      setPhotoError(
        'We couldn’t upload that photo. You can try again, or continue without one.'
      );
    } finally {
      setPhotoUploading(false);
    }
  };

  const removePhoto = () => {
    setPhotoKey(null);
    setPhotoUrl('');
    setPhotoError(null);
    setPhotoUsage('reference');
    setPhotoNote('');
  };

  const allFeelings = () => joinFeelings(feelings, ownFeeling);

  // Every answer the wizard collected, handed to the pure assembler. Adding a step, reordering the
  // steps, or making the review editable must not change this call — that is the hard requirement on
  // the create-flow rework, and generateRequest.guard.test.js is what enforces it.
  const buildRequest = () =>
    buildGenerateRequest({
      obituaryId,
      deceasedDetails,
      sessionId: getSessionId(),
      feelings,
      ownFeeling,
      styleId,
      imageryDescription,
      coverText,
      photoKey,
      photoUsage,
      photoNote,
      referenceCardId,
      changeInstruction,
      notifyWhenReady,
      notifyEmail,
    });

  const runGenerate = async () => {
    // Photo-on-card skips AI generation entirely — go straight to the single cover → personalize.
    if (isPhotoOnCard) return runPhotoCard();

    setError(null);
    setPhase('generating');
    let pendingId = null;
    try {
      // ASYNC (Step 3): enqueue the job, then poll the status endpoint until the worker finishes.
      const result = await postJSON('/api/cards/generate', buildRequest());
      // Metering soft cap (ENFORCE mode only): convert the value moment into a one-field email unlock —
      // never a paywall, never a dead end. The abuse floor (rate-floor) falls through to a normal error.
      if (result && result.success === false && result.data?.reason === 'soft-cap') {
        setCapReached(true);
        setPhase('wizard');
        setWizardStep(WIZARD_STEPS.length - 1);
        return;
      }
      if (!result || !result.success || !result.data?.generationId) {
        throw new Error(result?.error || 'Generation failed');
      }
      // Browse-away (Phase B): record an in-progress placeholder in "My creations" the moment the job is
      // enqueued, so the user can leave the wait screen and still find the design landing there. The
      // background job keeps running server-side regardless of what this page does.
      pendingId = result.data.generationId;
      addPendingCreation({
        generationId: pendingId,
        inputs: { source: 'generation', feeling: feelings, styleId, coverText: coverText.trim() || undefined },
      });

      const done = await pollGeneration(pendingId);
      // The status payload carries the generationId back so from-generation can reference it.
      setGeneration({ generationId: pendingId, ...done });
      setSelectedOptionId(null);
      setPhase('ideas');
    } catch (e) {
      // Backstop reached while the job is STILL running server-side: DON'T show a try-again (which would
      // invite a duplicate paid generation) and DON'T remove the placeholder — hand off to browse-away;
      // the finished design will land in My creations.
      if (e && e.message === POLL_STILL_WORKING) {
        navigate('/creations');
        return;
      }
      // A failed/abandoned enqueue shouldn't leave a dead "creating…" tile behind.
      if (pendingId) removePendingCreation(pendingId);
      setError(e.message || 'Something went wrong creating your designs.');
      // Return the shopper to where they were: the seeded "Make it yours" screen, or the wizard review.
      if (referenceCardId) {
        setPhase('makeYours');
      } else {
        setPhase('wizard');
        setWizardStep(WIZARD_STEPS.length - 1);
      }
    }
  };

  // "Place the actual photo on the card" (BR-CREATE-10): the photo becomes the card front. We send
  // it to the no-AI endpoint and go straight to personalize — there's nothing to pick. Print mechanics
  // (fit/bleed/crop) are handled server-side and never surfaced here.
  const runPhotoCard = async () => {
    setError(null);
    setPhase('generating');
    try {
      const result = await postJSON('/api/cards/from-photo', { photoKey, obituaryId });
      if (!result || !result.success || !result.data?.cardId) {
        throw new Error(result?.error || 'Could not prepare your card');
      }
      // Auto-save to My creations (local-first, invisible) so it's recoverable on this browser. Persist
      // the STABLE served cover (coverImageId + non-expiring coverImageUrl) — never the local blob: URL.
      addCreation({
        cardId: result.data.cardId,
        coverImageId: result.data.coverImageId || result.data.imageId || null,
        coverImageUrl: result.data.coverImageUrl || null,
        inputs: { source: 'photo', photoUsage: 'include' },
      });
      navigate(`/personalize/${result.data.cardId}`, {
        state: { stockType: 'standard', price: 14.99 },
      });
    } catch (e) {
      setError(e.message || 'Could not prepare your card. Please try again.');
      setPhase('wizard');
      setWizardStep(WIZARD_STEPS.length - 1);
    }
  };

  const useDesign = async () => {
    if (!generation || !selectedOptionId) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await postJSON('/api/cards/from-generation', {
        selectedOptionId,
        generationId: generation.generationId,
        obituaryId,
        provenance: generation.provenance,
        // Cover words + style so the backend bakes the real, style-matched type layer onto the cover.
        coverText: coverText.trim() || undefined,
        styleId,
      });
      if (!result || !result.success || !result.data?.cardId) {
        throw new Error(result?.error || 'Could not save your design');
      }
      // Finalized from the wait/ideas flow → drop the in-progress placeholder and save the real card.
      if (generation.generationId) removePendingCreation(generation.generationId);
      // Auto-save to My creations (local-first, invisible) so it's recoverable on this browser. Persist
      // the STABLE served cover (coverImageId + non-expiring coverImageUrl) — NOT the ephemeral option
      // preview URL, which is a 1 h presigned link that later renders as "preview unavailable".
      addCreation({
        cardId: result.data.cardId,
        coverImageId: result.data.coverImageId || result.data.imageId || null,
        coverImageUrl: result.data.coverImageUrl || null,
        inputs: { source: 'generation', feeling: feelings, styleId },
      });
      navigate(`/personalize/${result.data.cardId}`, {
        state: { stockType: 'standard', price: 14.99 },
      });
    } catch (e) {
      setError(e.message || 'Could not save your design. Please try again.');
      setSubmitting(false);
    }
  };

  // ---- Soft-cap reached (ENFORCE only): value-framed email-to-continue, NEVER a paywall ----------
  if (capReached) {
    return (
      <>
        <StoreHeader />
        <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
          <Typography component="h1" sx={{ fontSize: { xs: '1.4rem', md: '1.8rem' }, fontWeight: 700, mb: 1 }}>
            Loving these? Keep creating.
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Add your email to save your designs and keep creating — it only takes a moment.
          </Typography>
          <EmailRecoveryPrompt onCollected={() => { setCapReached(false); runGenerate(); }} />
          <Button onClick={() => { setCapReached(false); if (referenceCardId) { setPhase('makeYours'); } else { setPhase('wizard'); setWizardStep(WIZARD_STEPS.length - 1); } }} sx={{ color: TEAL, mt: 2 }}>
            Back
          </Button>
        </Container>
      </>
    );
  }

  // ---- Generating ----------------------------------------------------------
  if (phase === 'generating') {
    const reduced = prefersReducedMotion();
    const stillWorking = !isPhotoOnCard && resolveWaitStage(genElapsedMs) === 'still-working';
    return (
      <>
        <StoreHeader />
        <Container maxWidth="sm" sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
          {!reduced && <CircularProgress sx={{ color: TEAL, mb: 3 }} />}
          <Typography component="h1" sx={{ fontSize: '1.3rem', fontWeight: 600, mb: 1 }}>
            {isPhotoOnCard ? 'Preparing your card' : stillWorking ? 'Still working on your design' : 'Creating your design'}
          </Typography>
          <Typography color="text.secondary">
            {isPhotoOnCard
              ? 'We’re getting your photo ready. This only takes a moment.'
              : stillWorking
                ? 'Still working — this can take several minutes. You can keep browsing; your design will land in My creations, or tick “Email me when it’s ready”.'
                : 'We’re carefully composing your cover. This can take a couple of minutes.'}
          </Typography>

          {/* Browse-away affordance (Phase B): the wait is NOT a trap. Photo-on-card is instant, so this
              only applies to AI generation. The job keeps running in the background either way. */}
          {!isPhotoOnCard && (
            <Box sx={{ mt: 4 }}>
              <Typography color="text.secondary" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                {notifyWhenReady && notifyEmail.trim()
                  ? 'You can keep browsing — we’ll email you and save it to your creations when it’s ready.'
                  : 'You can keep browsing — we’ll save it to your creations when it’s ready.'}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate('/creations')}
                sx={{ color: TEAL, borderColor: TEAL }}
              >
                Keep browsing
              </Button>
            </Box>
          )}
        </Container>
      </>
    );
  }

  // ---- Ideas (options grid) ------------------------------------------------
  if (phase === 'ideas' && generation) {
    const quickViewOption = generation.options.find((o) => o.id === quickViewOptionId);
    return (
      <>
        <StoreHeader />
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
          <Button startIcon={<BackIcon />} onClick={() => { if (referenceCardId) { setPhase('makeYours'); } else { setPhase('wizard'); setWizardStep(WIZARD_STEPS.length - 1); } }} sx={{ color: TEAL, mb: 1 }}>
            Adjust
          </Button>
          <Typography component="h1" sx={{ fontSize: { xs: '1.4rem', md: '1.8rem' }, fontWeight: 700, mb: 0.5 }}>
            Let’s design your cover
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Your AI-generated cover. Tap it to see it large.
          </Typography>

          {/* Email recovery prompt sits at the TOP of this step — until they give us their email. */}
          <Box sx={{ mb: 3 }}>
            <EmailRecoveryPrompt />
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            {generation.options.map((opt, idx) => (
              <Box
                key={opt.id}
                component="button"
                type="button"
                aria-label={`Select design option ${idx + 1}`}
                onClick={() => { setSelectedOptionId(opt.id); setQuickViewOptionId(opt.id); }}
                sx={{
                  position: 'relative',
                  p: 0,
                  border: '2px solid',
                  borderColor: selectedOptionId === opt.id ? TEAL : 'transparent',
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  backgroundColor: 'grey.100',
                  // Cards are 5:7 — show the whole cover (no square crop) so the option matches the result.
                  aspectRatio: '5 / 7',
                }}
              >
                {/* The cover words are BAKED INTO the option image server-side — no CSS overlay. */}
                <Box component="img" src={opt.url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <Chip label="AI design" size="small" sx={{ position: 'absolute', top: 6, left: 6, backgroundColor: 'rgba(255,255,255,0.9)' }} />
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', gap: 1.5 }}>
            <Button variant="outlined" onClick={runGenerate} sx={{ color: TEAL, borderColor: TEAL }}>
              Regenerate
            </Button>
          </Box>
        </Container>

        {/* Tap an option → shared quick-view modal; the action proceeds to personalize. */}
        <CardQuickViewModal
          open={!!quickViewOptionId}
          onClose={() => setQuickViewOptionId(null)}
          coverImageUrl={quickViewOption ? quickViewOption.url : null}
          alt="Your card design"
          ariaLabel="Card design preview"
          actionLabel={submitting ? 'Saving…' : 'Use this design'}
          onConfirm={useDesign}
        />
      </>
    );
  }

  // ---- Make it yours (seeded from "Create one like this") ------------------
  // One screen, one question: the card's recipe is preselected silently and the card image is the
  // reference; the shopper optionally adds a photo and says what to change (empty = one just like it).
  if (phase === 'makeYours') {
    const photoIsLikeness = photoUsage === 'likeness';
    return (
      <>
        <StoreHeader />
        <Container maxWidth="sm" sx={{ py: { xs: 3, md: 5 }, pb: 8 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#a8a49a', mb: 1 }}>
            Make it yours
          </Typography>
          <Typography component="h1" sx={{ fontSize: { xs: '1.35rem', md: '1.6rem' }, fontWeight: 700, mb: 0.5 }}>
            Starting from this card
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.55 }}>
            We’ll match its look and feel. Tell us what to change — or change nothing and we’ll make you
            one just like it.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#a8a49a', mb: 1 }}>
            Your references
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
            {/* The source card — carried over automatically as the visual reference. */}
            <Box sx={{ position: 'relative', width: 84, flex: '0 0 auto' }}>
              <CardCoverImage
                cardId={referenceCardId}
                imageId={referenceCover.coverImageId}
                coverImageUrl={referenceCover.coverImageUrl}
                alt={referenceCover.name}
                sx={{ width: '100%', aspectRatio: '5 / 7', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ece5d4' }}
              />
              <Box sx={{ position: 'absolute', top: 5, left: 5, backgroundColor: TEAL, color: '#fff', fontSize: '0.6rem', fontWeight: 700, borderRadius: '4px', px: 0.6, py: 0.1 }}>
                CARD
              </Box>
            </Box>

            {/* Optional photo — a person's likeness or extra inspiration (reuses the create-flow upload). */}
            {photoUrl ? (
              <Box sx={{ position: 'relative', width: 84, flex: '0 0 auto' }}>
                <Box
                  component="img"
                  src={photoUrl}
                  alt="Your uploaded photo"
                  sx={{ width: '100%', aspectRatio: '5 / 7', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ece5d4' }}
                />
                <Button
                  onClick={removePhoto}
                  aria-label="Remove photo"
                  sx={{ position: 'absolute', top: 2, right: 2, minWidth: 0, p: 0.25, color: '#fff', backgroundColor: 'rgba(26,34,44,0.55)', '&:hover': { backgroundColor: 'rgba(26,34,44,0.75)' } }}
                >
                  ✕
                </Button>
              </Box>
            ) : (
              <Button
                component="label"
                disabled={photoUploading}
                sx={{ width: 84, flex: '0 0 auto', aspectRatio: '5 / 7', border: '1.5px dashed #b9c7cc', borderRadius: '8px', backgroundColor: '#f3f7f8', color: TEAL, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, textTransform: 'none', p: 0.5 }}
              >
                {photoUploading ? (
                  <CircularProgress size={18} />
                ) : (
                  <>
                    <Box component="span" sx={{ fontSize: '1.3rem', lineHeight: 1 }}>+</Box>
                    <Box component="span" sx={{ fontSize: '0.68rem', fontWeight: 600, lineHeight: 1.15, textAlign: 'center' }}>
                      Add a<br />photo
                    </Box>
                  </>
                )}
                <input data-testid="makeyours-photo-input" type="file" accept="image/*" hidden onChange={handlePhotoChange} />
              </Button>
            )}
          </Box>

          {photoKey ? (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button
                size="small"
                onClick={() => setPhotoUsage('likeness')}
                aria-pressed={photoIsLikeness}
                variant={photoIsLikeness ? 'contained' : 'outlined'}
                sx={{ textTransform: 'none', color: photoIsLikeness ? '#fff' : TEAL, borderColor: TEAL, backgroundColor: photoIsLikeness ? TEAL : 'transparent', '&:hover': { backgroundColor: photoIsLikeness ? '#1a566d' : 'rgba(32,104,132,0.06)' } }}
              >
                Include their likeness
              </Button>
              <Button
                size="small"
                onClick={() => setPhotoUsage('reference')}
                aria-pressed={!photoIsLikeness}
                variant={!photoIsLikeness ? 'contained' : 'outlined'}
                sx={{ textTransform: 'none', color: !photoIsLikeness ? '#fff' : TEAL, borderColor: TEAL, backgroundColor: !photoIsLikeness ? TEAL : 'transparent', '&:hover': { backgroundColor: !photoIsLikeness ? '#1a566d' : 'rgba(32,104,132,0.06)' } }}
              >
                Just inspiration
              </Button>
            </Box>
          ) : (
            <Typography sx={{ color: '#8a8f8c', fontSize: '0.8rem', lineHeight: 1.45, mb: 2 }}>
              The card is carried over automatically. Add a photo to include someone’s likeness or as extra
              inspiration — optional.
            </Typography>
          )}

          {photoError && <Alert severity="warning" sx={{ mb: 2 }}>{photoError}</Alert>}

          <Typography sx={{ fontSize: '1rem', fontWeight: 700, mb: 0.25 }}>
            What would you like to change?
          </Typography>
          <Typography sx={{ color: '#8a8f8c', fontSize: '0.8rem', mb: 1 }}>
            Say it however you’d say it to a friend.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={changeInstruction}
            onChange={(e) => setChangeInstruction(e.target.value)}
            placeholder="e.g. “Make it say ‘Forever in our hearts’ and add the name Erik Halvorsen. Keep everything else.”"
            inputProps={{ 'data-testid': 'change-instruction' }}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, mb: 3 }}>
            {CHANGE_CHIPS.map((chip) => (
              <Chip
                key={chip.label}
                label={chip.label}
                onClick={() => appendChangeStarter(chip.starter)}
                variant="outlined"
                sx={{ borderColor: '#dfe3e4', color: '#41545c', backgroundColor: '#fff' }}
              />
            ))}
          </Box>

          <Button
            fullWidth
            variant="contained"
            startIcon={<CreateIcon />}
            onClick={runGenerate}
            sx={{ backgroundColor: TEAL, py: 1.4, '&:hover': { backgroundColor: '#1a566d' } }}
          >
            Create my card
          </Button>
          <Box sx={{ textAlign: 'center', mt: 1.5 }}>
            <Button
              onClick={() => { setPhase('wizard'); setWizardStep(0); }}
              sx={{ color: TEAL, textTransform: 'none' }}
            >
              Prefer to set it yourself? Open the full builder ›
            </Button>
          </Box>
        </Container>
      </>
    );
  }

  // ---- Wizard --------------------------------------------------------------
  const step = WIZARD_STEPS[wizardStep];
  const isLast = wizardStep === WIZARD_STEPS.length - 1;

  const goNext = () => {
    if (isLast) {
      runGenerate();
    } else {
      setWizardStep((s) => s + 1);
    }
  };
  const goBack = () => {
    if (wizardStep === 0) {
      // No intro screen anymore — the how-it-works modal is the front door, so step 1 backs out home.
      navigate('/');
    } else {
      setWizardStep((s) => s - 1);
    }
  };

  return (
    <>
      <StoreHeader />
      <Container maxWidth="sm" sx={{ py: { xs: 3, md: 5 }, pb: 12 }}>
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 2 }}>
          Step {wizardStep + 1} of {WIZARD_STEPS.length}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 'photo' && (
          <Box>
            <Typography component="h1" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 1 }}>
              Add a photo <Typography component="span" color="text.secondary" sx={{ fontWeight: 400, fontSize: '0.9rem' }}>(optional)</Typography>
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2, lineHeight: 1.55 }}>
              A photo can be someone to include, a place to reference, or anything to build from.
              You can also let us work from a likeness.
            </Typography>

            {photoUrl ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box
                  component="img"
                  src={photoUrl}
                  alt="Your uploaded photo"
                  sx={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                />
                <Button onClick={removePhoto} sx={{ color: 'text.secondary' }}>
                  Remove
                </Button>
              </Box>
            ) : (
              <Button
                component="label"
                variant="outlined"
                disabled={photoUploading}
                startIcon={photoUploading ? <CircularProgress size={18} /> : undefined}
                sx={{ color: TEAL, borderColor: TEAL, '&:hover': { backgroundColor: 'rgba(32,104,132,0.06)' } }}
              >
                {photoUploading ? 'Uploading…' : 'Add a photo'}
                <input
                  data-testid="photo-input"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handlePhotoChange}
                />
              </Button>
            )}

            {photoError && (
              <Alert severity="warning" sx={{ mt: 2 }}>{photoError}</Alert>
            )}

            {photoKey && (
              <Box sx={{ mt: 3 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  How should we use this photo?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  {PHOTO_USAGE_OPTIONS.map((opt) => {
                    const selected = photoUsage === opt.id;
                    return (
                      <Button
                        key={opt.id}
                        onClick={() => { if (!opt.disabled) setPhotoUsage(opt.id); }}
                        disabled={opt.disabled}
                        aria-pressed={selected}
                        variant={selected ? 'contained' : 'outlined'}
                        sx={{
                          textTransform: 'none',
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          flexDirection: 'column',
                          px: 1.75,
                          py: 1.25,
                          color: selected ? '#fff' : TEAL,
                          borderColor: TEAL,
                          backgroundColor: selected ? TEAL : 'transparent',
                          '&:hover': { backgroundColor: selected ? '#1a566d' : 'rgba(32,104,132,0.06)' },
                        }}
                      >
                        <Typography component="span" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {opt.label}{opt.disabled ? ' — Coming soon' : ''}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{ fontSize: '0.78rem', fontWeight: 400, mt: 0.25, opacity: 0.85, lineHeight: 1.3 }}
                        >
                          {opt.help}
                        </Typography>
                      </Button>
                    );
                  })}
                </Box>
                <TextField
                  fullWidth
                  label="Or tell us exactly how to use this photo (optional)"
                  value={photoNote}
                  onChange={(e) => setPhotoNote(e.target.value)}
                  sx={{ mt: 2 }}
                  placeholder="e.g. Keep his glasses, soften the background."
                />
              </Box>
            )}
          </Box>
        )}

        {step === 'feeling' && (
          <FeelingStep
            selected={feelings}
            onToggle={toggleFeeling}
            ownFeeling={ownFeeling}
            onOwnFeeling={setOwnFeeling}
          />
        )}

        {step === 'style' && <StyleStep styleId={styleId} onSelect={setStyleId} />}

        {step === 'words' && (
          <Box>
            <Typography component="h1" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 1 }}>
              Your words &amp; details
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Two separate things: the words printed on the cover, and what you’d like the artwork to show.
            </Typography>

            <SuggestionField
              kind="words"
              label="Words on the cover"
              help="Type anything you like — or let us suggest wording based on the feeling and style you chose."
              value={coverText}
              onChange={setCoverText}
              placeholder="e.g. In loving memory of Robert"
              serif
              feeling={allFeelings()}
              styleId={styleId}
            />

            <Box sx={{ mt: 3 }}>
              <SuggestionField
                kind="imagery"
                label="What would you like to see?"
                help="Describe the imagery only — a place, a flower, their faith, a color. We keep words out of the art."
                value={imageryDescription}
                onChange={setImageryDescription}
                placeholder="e.g. A calm lake at dawn, a small fishing boat, soft muted colors."
                multiline
                feeling={allFeelings()}
                styleId={styleId}
              />
            </Box>
          </Box>
        )}

        {step === 'review' && (
          <Box>
            <Typography component="h1" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 1 }}>
              Review
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Here’s what we’ll use to design your card.
            </Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 1 }}>
              <Typography sx={{ mb: 1 }}><strong>Words on cover:</strong> {coverText.trim() || '— none —'}</Typography>
              <Typography sx={{ mb: 1 }}><strong>Feeling:</strong> {[...feelings, ...(ownFeeling.trim() ? [`“${ownFeeling.trim()}”`] : [])].join(', ') || 'None selected'}</Typography>
              <Typography sx={{ mb: 1 }}><strong>Style:</strong> {styleId || 'None'}</Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Photo:</strong>{' '}
                {photoKey ? (PHOTO_USAGE_LABELS[photoUsage] || photoUsage) : 'None added'}
              </Typography>
              {photoKey && photoNote.trim() && (
                <Typography sx={{ mb: 1 }}><strong>Photo note:</strong> {photoNote.trim()}</Typography>
              )}
              <Typography><strong>Artwork shows:</strong> {imageryDescription.trim() || 'None added'}</Typography>
            </Box>

            {/* Browse-away opt-in (Phase B): designing takes a couple of minutes — offer to email them
                when it's ready so they never have to wait on a spinner. Explicit opt-in only. */}
            {!isPhotoOnCard && (
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notifyWhenReady}
                      onChange={(e) => setNotifyWhenReady(e.target.checked)}
                      sx={{ color: TEAL, '&.Mui-checked': { color: TEAL } }}
                    />
                  }
                  label="Email me when it’s ready (optional)"
                />
                {notifyWhenReady && (
                  <TextField
                    fullWidth
                    type="email"
                    label="Your email address"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    placeholder="you@example.com"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
          <Button onClick={goBack} sx={{ color: 'text.secondary' }}>
            Back
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={goNext}
            startIcon={isLast ? <CreateIcon /> : undefined}
            sx={{ backgroundColor: TEAL, '&:hover': { backgroundColor: '#1a566d' } }}
          >
            {isLast ? (isPhotoOnCard ? 'Create my card' : 'Generate options') : 'Next'}
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default CreatePage;
